import { ConnectError, createClient } from '@connectrpc/connect';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ProductService } from 'proto/gen/ts/shirt/v1/product_pb';
import { useState } from 'react';
import { transport } from '../utils/transport';
const productClient = createClient(ProductService, transport);
/**
 * Submits an email to ProductService.SubscribeProductUpdates and tracks UI state.
 *
 * State machine: idle → submitting → success | already | error.
 * `success` and `already` are both successful outcomes — the distinction is for copy
 * ("you're on the list" vs "already on the list, see you at launch").
 *
 * Invalidates the matching ['products', ...] react-query caches on success so the
 * editorial catalog page updates its subscriber count without a full refetch dance.
 */ export function useNotifySubscription(productId, productSlug) {
    const queryClient = useQueryClient();
    const [state, setState] = useState('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [subscriberCount, setSubscriberCount] = useState(null);
    const mutation = useMutation({
        mutationFn: (email)=>productClient.subscribeProductUpdates({
                productId,
                slug: productSlug ?? '',
                email
            }),
        onSuccess: (res)=>{
            setSubscriberCount(res.subscriberCount);
            setState(res.created ? 'success' : 'already');
            // Refresh any product list / detail queries that include subscriber counts.
            queryClient.invalidateQueries({
                queryKey: [
                    'products'
                ]
            });
        },
        onError: (err)=>{
            const msg = err instanceof ConnectError ? humanizeConnectError(err) : err instanceof Error ? err.message : 'Something went wrong. Try again in a moment.';
            setErrorMessage(msg);
            setState('error');
        }
    });
    return {
        state,
        errorMessage,
        subscriberCount,
        submit: async (email)=>{
            setState('submitting');
            setErrorMessage('');
            await mutation.mutateAsync(email).catch(()=>{
            // onError already handled state transition; swallow so the caller can `await` safely.
            });
        },
        reset: ()=>{
            setState('idle');
            setErrorMessage('');
        }
    };
}
function humanizeConnectError(err) {
    // ConnectError messages are prefixed like "[invalid_argument] invalid email" — strip
    // the bracketed code so users see something readable.
    const raw = err.rawMessage || err.message;
    if (raw.toLowerCase().includes('invalid email')) return "That doesn't look like a valid email.";
    if (raw.toLowerCase().includes('already shipping')) return 'This product is already shipping — head to its page to order.';
    return 'Something went wrong. Try again in a moment.';
}
